import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Calendar as CalendarIcon, Filter, ExternalLink, FileDown, AlertCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from "@/i18n/translations";
import { API } from "@/App";
import { toast } from "sonner";
import axios from "axios";

const normPhone = (p) => String(p || '').replace(/\D/g, '').slice(-10);

export const isRepeatBooking = (booking, allBookings) => {
  const ph = normPhone(booking.phone || booking.client_phone);
  if (!ph) return false;
  return allBookings.some(
    (b) => b !== booking &&
      normPhone(b.phone || b.client_phone) === ph &&
      new Date(b.created_at) < new Date(booking.created_at)
  );
};

export const bookingChannel = (b) => {
  if (["instagram", "whatsapp", "messenger", "twilio_call", "phone"].includes(b.source)) return b.source;
  const s = b.session_id || "";
  if (s.startsWith("phone_")) return "phone";
  if (s.startsWith("zn_") || s.startsWith("ig_") || s.startsWith("meta_instagram")) return "instagram";
  if (s.startsWith("greenapi_") || s.startsWith("meta_whatsapp")) return "whatsapp";
  if (s.startsWith("meta_messenger")) return "messenger";
  return "widget";
};

const CHANNEL_META = {
  instagram: { label: "Instagram", icon: "📷", cls: "bg-pink-100 text-pink-700 border-pink-300" },
  whatsapp: { label: "WhatsApp", icon: "📲", cls: "bg-green-100 text-green-700 border-green-300" },
  messenger: { label: "Messenger", icon: "💬", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  twilio_call: { label: "Call", icon: "📞", cls: "bg-purple-100 text-purple-700 border-purple-300" },
  phone: { label: "Phone AI", icon: "📞", cls: "bg-purple-100 text-purple-700 border-purple-300" },
  widget: { label: "Widget", icon: "🌐", cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

export const ChannelBadge = ({ booking, index }) => {
  const ch = bookingChannel(booking);
  const m = CHANNEL_META[ch] || CHANNEL_META.widget;
  return (
    <span
      data-testid={`booking-channel-badge-${index}`}
      className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-xs font-semibold align-middle ${m.cls}`}
    >
      {m.icon} {m.label}
    </span>
  );
};

export const countUniqueClients = (allBookings) => {
  const set = new Set();
  allBookings.forEach((b) => {
    const ph = normPhone(b.phone || b.client_phone);
    if (ph) set.add(ph);
    else set.add(`no-phone-${b.id || Math.random()}`);
  });
  return set.size;
};

export default function BookingsAnalytics({ 
  bookings, 
  analytics, 
  dateFilter, 
  setDateFilter,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  onRefresh,
  subscriptionInfo
}) {
  const { t } = useTranslation();
  const uniqueClientsCount = countUniqueClients(bookings);
  
  // Export to Excel handler
  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/bookings/export/excel`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `chatity_bookings_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('bookings.exportSuccess') || 'Заявки успешно экспортированы');
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 403) {
        toast.error(t('bookings.exportUnlimitedOnly') || 'Экспорт доступен только для тарифа Unlimited');
      } else {
        toast.error(t('bookings.exportError') || 'Ошибка экспорта');
      }
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
  };

  // Get source display name
  const getSourceDisplay = (source) => {
    if (!source || source === 'Прямой заход') return t('bookings.directVisit');
    return source;
  };

  return (
    <div className="space-y-6">
      {/* Storage Info Banner */}
      <div className={`p-4 rounded-lg border-2 ${subscriptionInfo?.plan === 'unlimited' ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${subscriptionInfo?.plan === 'unlimited' ? 'text-green-600' : 'text-yellow-600'}`} />
          <div className="flex-1">
            <p className="font-semibold text-black mb-1">
              {t('bookings.storageInfo') || '📋 Заявки хранятся 30 дней'}
            </p>
            <p className="text-sm text-gray-700">
              {subscriptionInfo?.plan === 'unlimited' 
                ? (t('bookings.unlimitedStorageInfo') || 'Используйте экспорт в Excel для долгосрочного хранения истории заявок')
                : (t('bookings.upgradeForExport') || 'Обновитесь до тарифа Unlimited для экспорта заявок в Excel')}
            </p>
          </div>
          {subscriptionInfo?.plan === 'unlimited' && (
            <Button
              onClick={handleExportExcel}
              className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {t('bookings.exportExcel') || 'Экспорт в Excel'}
            </Button>
          )}
        </div>
      </div>

      {/* Date Filter */}
      <Card className="bg-white text-black border-2 border-black">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">{t('bookings.period')}</label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="border-2 border-black text-black">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('bookings.today')}</SelectItem>
                  <SelectItem value="7">{t('bookings.last7days')}</SelectItem>
                  <SelectItem value="30">{t('bookings.last30days')}</SelectItem>
                  <SelectItem value="custom">{t('bookings.customDates')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateFilter === 'custom' && (
              <>
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-2 block">{t('bookings.from')}</label>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="border-2 border-black text-black"
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-2 block">{t('bookings.to')}</label>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="border-2 border-black text-black"
                  />
                </div>
              </>
            )}

            <Button
              onClick={onRefresh}
              variant="outline"
              className="border-2 border-black hover:bg-gray-100"
            >
              {t('bookings.refresh')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-2 border-black">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600 font-medium">{t('bookings.todayCount')}</p>
                  <p className="text-3xl font-bold text-black mt-2">{analytics.today_count}</p>
                </div>
                <CalendarIcon className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-black">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600 font-medium">{t('bookings.yesterdayCount')}</p>
                  <p className="text-3xl font-bold text-black mt-2">{analytics.yesterday_count}</p>
                </div>
                <CalendarIcon className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-black">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600 font-medium">{t('bookings.growthPercent')}</p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <p className={`text-3xl font-bold ${analytics.growth_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analytics.growth_percent > 0 ? '+' : ''}{analytics.growth_percent}%
                    </p>
                  </div>
                </div>
                {analytics.growth_percent >= 0 ? (
                  <TrendingUp className="w-8 h-8 text-green-600" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-600" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Line Chart */}
      {analytics && analytics.daily_stats && analytics.daily_stats.length > 0 && (
        <Card className="bg-white text-black border-2 border-black">
          <CardHeader>
            <CardTitle>{t('bookings.dynamicsTitle')}</CardTitle>
            <CardDescription className="text-gray-600">
              {t('bookings.dynamicsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.daily_stats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="#000"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#000"
                    style={{ fontSize: '12px' }}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '2px solid #000',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(value) => formatDate(value)}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#000" 
                    strokeWidth={3}
                    dot={{ fill: '#000', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* UTM Sources Table */}
      {analytics && analytics.utm_sources && analytics.utm_sources.length > 0 && (
        <Card className="bg-white text-black border-2 border-black">
          <CardHeader>
            <CardTitle>{t('bookings.topSourcesTitle')}</CardTitle>
            <CardDescription className="text-gray-600">
              {t('bookings.topSourcesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-black">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold text-black">{t('bookings.source')}</th>
                    <th className="px-4 py-3 text-left font-bold text-black">{t('analytics.channel') || 'Канал'}</th>
                    <th className="px-4 py-3 text-left font-bold text-black">{t('analytics.campaign') || 'Кампания'}</th>
                    <th className="px-4 py-3 text-right font-bold text-black">{t('bookings.bookingsCount')}</th>
                    <th className="px-4 py-3 text-right font-bold text-black">%</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.utm_sources.map((source, index) => (
                    <tr 
                      key={index}
                      className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-4 py-3 font-medium text-black">{getSourceDisplay(source.source)}</td>
                      <td className="px-4 py-3 text-gray-600">{source.medium}</td>
                      <td className="px-4 py-3 text-gray-600">{source.campaign}</td>
                      <td className="px-4 py-3 text-right font-bold text-black">{source.count}</td>
                      <td className="px-4 py-3 text-right text-black">
                        {Math.round((source.count / analytics.total_count) * 100)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bookings List */}
      <Card className="bg-white text-black border-2 border-black">
        <CardHeader>
          <CardTitle className="text-2xl">{t('bookings.title')}</CardTitle>
          <CardDescription className="text-gray-600" data-testid="bookings-counters">
            {t('bookings.totalCount') || 'Всего заявок'}: {bookings.length} • {t('bookings.uniqueClients') || 'Уникальных клиентов'}: {uniqueClientsCount}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {t('bookings.noBookings')}
            </p>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <div
                  key={booking.id}
                  className="border-2 border-black p-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-semibold">{t('bookings.client')}:</span> {booking.client_name}
                      {isRepeatBooking(booking, bookings) && (
                        <span data-testid={`repeat-badge-${index}`} className="ml-2 inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-300 rounded-full text-xs font-semibold">
                          🔁 {t('bookings.repeatBadge') || 'Повторная'}
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">{t('bookings.phone')}:</span> {booking.phone}
                    </div>
                    <div className="col-span-2">
                      <span className="font-semibold">{t('bookings.service')}:</span> {booking.service}
                    </div>
                    <div>
                      <span className="font-semibold">{t('bookings.date')}:</span> {booking.preferred_date}
                    </div>
                    <div>
                      <span className="font-semibold">{t('bookings.created')}:</span>{" "}
                      {new Date(booking.created_at).toLocaleString('ru-RU')}
                    </div>
                    {(booking.branch_name || booking.staff_name) && (
                      <div className="col-span-2">
                        {booking.branch_name && <span data-testid={`booking-branch-${index}`} className="mr-3">📍 <strong>{booking.branch_name}</strong></span>}
                        {booking.staff_name && (
                          <span data-testid={`booking-master-${index}`}>👤 <strong>{booking.staff_name}</strong>
                            {booking.slot_confirmed && booking.start_at && (
                              <span className="ml-2 text-green-700 font-semibold">✅ {booking.start_at.replace('T', ' ')}</span>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="col-span-2">
                      <span className="font-semibold">Источник:</span>{" "}
                      <ChannelBadge booking={booking} index={index} />
                      {booking.utm_source && booking.utm_source !== bookingChannel(booking) && (
                        <span className="ml-1 inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {getSourceDisplay(booking.utm_source)}
                          {booking.utm_medium && ` / ${booking.utm_medium}`}
                        </span>
                      )}
                    </div>
                    {booking.reschedule_requested && (
                      <div className="col-span-2 text-orange-700 font-semibold">
                        🔁 {booking.reschedule_note ? `«${booking.reschedule_note}»` : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
